import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Table,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { getItemHistory } from '../api';
import './memos.scss';

const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const truncateValue = (value, maxLength = 100) => {
  const formatted = formatValue(value);
  return formatted.length > maxLength ? `${formatted.substring(0, maxLength)}...` : formatted;
};

const formatTimestamp = (date) => {
  const now = new Date();
  const memoDate = new Date(date);
  const isToday =
    now.getFullYear() === memoDate.getFullYear() &&
    now.getMonth() === memoDate.getMonth() &&
    now.getDate() === memoDate.getDate();

  const hours = memoDate.getHours();
  const minutes = memoDate.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const timeString = `${formattedHours}:${formattedMinutes} ${period}`;

  return isToday
    ? `Today ${timeString}`
    : memoDate.toLocaleString('en-US', {
      timeZone: 'America/Denver',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
};

const Filter = ({ fieldFilter, onFieldFilterChange, onClearFilters, availableFields }) => (
  <div className="d-flex align-items-center gap-2">
    <select
      value={fieldFilter}
      onChange={(e) => onFieldFilterChange(e.target.value)}
      className="form-select form-select-sm"
      style={{ width: '160px' }}
    >
      <option value="">All Fields</option>
      {availableFields.map((field) => (
        <option key={field} value={field}>
          {field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
        </option>
      ))}
    </select>
    {fieldFilter && (
      <Button color="outline-secondary" size="sm" onClick={onClearFilters}>
        <FeatherIcon icon="x" size={14} />
      </Button>
    )}
  </div>
);

const Loading = () => (
  <div className="text-center py-4">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
    <p className="mt-2 text-muted">Loading...</p>
  </div>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="d-flex justify-content-center mt-3">
      <nav className="pagination-nav d-flex align-items-center gap-2">
        <Button
          color="outline-secondary"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => onPageChange(null, currentPage - 1)}
        >
          Previous
        </Button>
        <span className="px-3 py-1 bg-primary text-white rounded">{currentPage}</span>
        <Button
          color="outline-secondary"
          size="sm"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(null, currentPage + 1)}
        >
          Next
        </Button>
      </nav>
    </div>
  );
};

const MemosTable = ({ itemId, fetchMemos, fieldFilter }) => {
  const [memos, setMemos] = useState([]);
  const [filteredMemos, setFilteredMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const memosPerPage = 10;

  const totalPages = Math.ceil(filteredMemos.length / memosPerPage);

  const fetchMemosData = async (fieldName = '') => {
    setLoading(true);
    setError(null);
    try {
      if (!itemId) {
        console.warn('Missing itemId for memos fetch');
        setMemos([]);
        setFilteredMemos([]);
        setLoading(false);
        return;
      }

      const response = await getItemHistory(itemId, {});
      if (!response || !response.history) {
        throw new Error('Failed to fetch change history');
      }

      const fetchedMemos = response.history || [];
      setMemos(fetchedMemos);

      const filtered = fieldName
        ? fetchedMemos.filter((memo) => memo.field_name?.toLowerCase() === fieldName.toLowerCase())
        : fetchedMemos;
      setFilteredMemos(filtered);
      setCurrentPage(1); // Reset to first page on new data
    } catch (err) {
      console.error('Error fetching memos:', err);
      setError(err.message || 'Error fetching change history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemosData(fieldFilter);
  }, [itemId, fieldFilter]);

  useEffect(() => {
    if (fetchMemos) {
      fetchMemos.current = () => fetchMemosData(fieldFilter);
    }
  }, [fetchMemos, fieldFilter]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const indexOfLastMemo = currentPage * memosPerPage;
  const indexOfFirstMemo = indexOfLastMemo - memosPerPage;
  const currentMemos = filteredMemos.slice(indexOfFirstMemo, indexOfLastMemo);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Card className="my-3 border-danger shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-danger">
              <strong>Error:</strong> {error}
            </div>
            <Button
              color="danger"
              size="sm"
              onClick={() => fetchMemosData(fieldFilter)}
            >
              Retry
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      {filteredMemos.length === 0 ? (
        <div className="text-center py-4">
          <FeatherIcon icon="clock" size="28" className="text-muted mb-2" />
          <p className="text-muted mb-0">No change history found</p>
        </div>
      ) : (
        <>
          <Table responsive hover className="table-bordered memos-table">
            <thead className="table-light">
              <tr>
                <th style={{ width: '15%' }}>Changed By</th>
                <th style={{ width: '10%' }}>Role</th>
                <th style={{ width: '15%' }}>Timestamp</th>
                <th style={{ width: '15%' }}>Field</th>
                <th style={{ width: '25%' }}>New Value</th>
                <th style={{ width: '20%' }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {currentMemos.map((memo) => (
                <tr key={memo._id || memo.changed_at}>
                  <td>{memo.changedByName || 'N/A'}</td>
                  <td>{memo.role || 'N/A'}</td>
                  <td>{formatTimestamp(memo.changed_at)}</td>
                  <td>{memo.field_name?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'N/A'}</td>
                  <td>{truncateValue(memo.new_value)}</td>
                  <td>{memo.change_reason || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

const MemosWrapper = ({ itemId }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [fieldFilter, setFieldFilter] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const fetchMemosRef = useRef(null);

  const hasRequiredProps = Boolean(itemId);

  const toggleCollapse = () => setIsOpen((prev) => !prev);

  const handleFieldFilterChange = (value) => {
    setFieldFilter(value);
  };

  const handleClearFilters = () => {
    setFieldFilter('');
  };

  useEffect(() => {
    if (hasRequiredProps && isOpen) {
      const getAvailableFields = async () => {
        try {
          const response = await getItemHistory(itemId, { page: 1, limit: 1 });
          if (response && response.history) {
            const historyFields = response.history?.map((memo) => memo.field_name?.toLowerCase()) || [];
            const defaultFields = ['comments', 'status'];
            const fields = defaultFields.filter((field) => historyFields.includes(field) || true);
            setAvailableFields([...new Set(fields)]);
          }
        } catch (err) {
          console.error('Error fetching available fields:', err);
        }
      };
      getAvailableFields();
    }
  }, [itemId, hasRequiredProps, isOpen]);

  if (!hasRequiredProps) {
    return (
      <Card className="mb-4">
        <CardHeader className="py-3">
          <div className="d-flex justify-content-center align-items-center position-relative">
            <Button
              color="primary"
              outline
              className="fw-bold px-4 py-2 rounded-pill"
              onClick={toggleCollapse}
            >
              <FeatherIcon icon="clock" className="me-2" size={16} />
              Change History
            </Button>
            <small className="text-muted position-absolute end-0">Select an item to view its change history</small>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardHeader className="py-3">
        <div className="d-flex justify-content-center align-items-center position-relative">
          <Button
            color="primary"
            outline
            className="fw-bold px-4 py-2 rounded-pill"
            onClick={toggleCollapse}
          >
            <FeatherIcon icon="clock" className="me-2" size={16} />
            Change History
          </Button>
          {hasRequiredProps && availableFields.length > 0 && (
            <div className="position-absolute end-0">
              <Filter
                fieldFilter={fieldFilter}
                onFieldFilterChange={handleFieldFilterChange}
                onClearFilters={handleClearFilters}
                availableFields={availableFields}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody className="p-3">
          <MemosTable
            itemId={itemId}
            fetchMemos={fetchMemosRef}
            fieldFilter={fieldFilter}
            onFieldFilterChange={handleFieldFilterChange}
            onClearFilters={handleClearFilters}
          />
        </CardBody>
      </Collapse>
    </Card>
  );
};

export { MemosWrapper as Memos };